import React, { createRef } from 'react';
import { MultiSelectComponent, CheckBoxSelection, Inject, FilteringEventArgs } from '@syncfusion/ej2-react-dropdowns';
import { Query } from '@syncfusion/ej2-data';
import { displayApiError } from '@shared/api/apiClient';
import { getFlatRemoteSearchResults, RemoteSearchParameters, RemoteSearchResult } from '@shared/api/remoteSearch';

interface MultiSelectProps {
    displayProp?: string;
    keyProp?: string;
    datasource?: any[];
    url?: string;
    parameters?: RemoteSearchParameters;
    searchProperty?: string;
    minLength: number;
    onClick?: (data: any[]) => void;
    onSelect?: (data: any, selected: boolean) => void;
    canSelect?: (data: any, selected: boolean) => boolean;
    templateFunction?: any;
    template?: string;
    btnLabel?: string;
    numParameter?: string;
    values?: any[];
    valueProp?: string;
    id?: string;
    preventInternalSelect?: boolean;
}

interface MultiSelectState {
    dataSource: any[];
    selectedValues: any[];
}

class MultiSelect extends React.Component<MultiSelectProps, MultiSelectState> {
    private _msRef = createRef<MultiSelectComponent>();
    private _remoteSearchRequestId = 0;

    constructor(props: MultiSelectProps) {
        super(props);

        this.state = {
            dataSource: props.datasource ? [...props.datasource] : [],
            selectedValues: this._normalizeValues(props.values || []),
        };
    }

    private _onFiltering = (e: FilteringEventArgs) => {
        if (this.props.minLength > 0 && (!e.text || e.text.length < this.props.minLength)) {
            e.cancel = true;
            return;
        }

        if (this.props.url) {
            e.preventDefaultAction = true;
            const requestId = ++this._remoteSearchRequestId;

            void getFlatRemoteSearchResults<RemoteSearchResult>({
                url: this.props.url,
                parameters: this.props.parameters,
                searchProperty: this.props.searchProperty,
                searchText: e.text,
                numParameter: this.props.numParameter,
                selectedCount: this.state.selectedValues.length,
            })
                .then((results) => {
                    if (requestId === this._remoteSearchRequestId) {
                        e.updateData(results, new Query());
                    }
                })
                .catch((error) => {
                    if (requestId === this._remoteSearchRequestId) {
                        e.updateData([], new Query());
                    }
                    displayApiError(error);
                });
        } else {
            // Client-side filtering on local datasource
            const query = new Query();
            if (e.text) {
                const field = this.props.displayProp || 'text';
                e.updateData(this.props.datasource, query.where(field, 'contains', e.text, true));
            } else {
                e.updateData(this.props.datasource, query);
            }
        }
    }

    // ---- "Add" button mode (onClick pattern) ----

    private _onButtonClick = () => {
        if (!this.props.onClick) return;

        const ms = this._msRef.current;
        if (!ms) return;

        // Get the full data items for the selected values
        const selectedItems: any[] = [];
        const selectedVals = ms.value || [];
        const allData = (ms as any).listData || ms.getItems().map((_: any, i: number) => (ms as any).getDataByValue(selectedVals[i]));

        for (const val of selectedVals) {
            const item = (ms as any).getDataByValue(val);
            if (item) {
                selectedItems.push(item);
            }
        }

        this.props.onClick(selectedItems);

        // Clear selection after "Add"
        ms.value = [];
    }

    private _normalizeValues = (values: any[] = []) => {
        return values.filter((value, index, source) => source.findIndex(candidate => candidate === value) === index);
    }

    private _areValuesEqual = (left: any[] = [], right: any[] = []) => {
        if (left.length !== right.length) {
            return false;
        }

        return left.every((value, index) => value === right[index]);
    }

    private _containsValue = (values: any[] = [], candidate: any) => {
        return values.some(value => value === candidate);
    }

    private _getItemByValue = (value: any) => {
        const multiSelect = this._msRef.current as any;
        if (multiSelect?.getDataByValue) {
            const item = multiSelect.getDataByValue(value);
            if (item) {
                return item;
            }
        }

        const field = this.props.keyProp || this.props.valueProp;
        if (!field) {
            return value;
        }

        return this.state.dataSource.find(item => item?.[field] === value);
    }

    private _emitSelectionDiff = (oldValues: any[] = [], newValues: any[] = []) => {
        if (!this.props.onSelect) {
            return;
        }

        this._normalizeValues(newValues)
            .filter(value => !this._containsValue(oldValues, value))
            .forEach(value => {
                const item = this._getItemByValue(value);
                if (item !== undefined) {
                    this.props.onSelect(item, true);
                }
            });

        this._normalizeValues(oldValues)
            .filter(value => !this._containsValue(newValues, value))
            .forEach(value => {
                const item = this._getItemByValue(value);
                if (item !== undefined) {
                    this.props.onSelect(item, false);
                }
            });
    }

    private _canSelect = (itemData: any, selected: boolean) => {
        if (!this.props.canSelect) {
            return true;
        }

        return this.props.canSelect(itemData, selected) !== false;
    }

    private _onSelectingHandler = (args: any) => {
        if (!this._canSelect(args.itemData, true)) {
            args.cancel = true;
        }
    }

    private _onRemovingHandler = (args: any) => {
        if (!this._canSelect(args.itemData, false)) {
            args.cancel = true;
        }
    }

    private _onChangeHandler = (args: any) => {
        const nextValues = this._normalizeValues(args.value || []);
        const previousValues = this._normalizeValues(this.state.selectedValues);

        if (!this._areValuesEqual(previousValues, nextValues)) {
            this._emitSelectionDiff(previousValues, nextValues);
        }

        this.setState({ selectedValues: nextValues });
    }

    // ---- Lifecycle ----

    UNSAFE_componentWillReceiveProps(nextProps: MultiSelectProps) {
        if (nextProps.datasource && JSON.stringify(nextProps.datasource) !== JSON.stringify(this.props.datasource)) {
            this.setState({ dataSource: [...nextProps.datasource] });
        }
        if (nextProps.values !== undefined && JSON.stringify(nextProps.values) !== JSON.stringify(this.props.values)) {
            this.setState({ selectedValues: this._normalizeValues(nextProps.values || []) });
        }
    }

    shouldComponentUpdate(nextProps: MultiSelectProps, nextState: MultiSelectState) {
        // Always re-render when state changes
        if (nextState !== this.state) return true;
        // Re-render when onClick appears/disappears (button visibility)
        if (!!nextProps.onClick !== !!this.props.onClick) return true;
        // Re-render when datasource changes
        if (nextProps.datasource !== this.props.datasource) return true;
        // Re-render when values change
        if (nextProps.values !== this.props.values) return true;
        return false;
    }

    render() {
        const fields: any = {};
        if (this.props.displayProp) {
            fields.text = this.props.displayProp;
        }
        if (this.props.keyProp) {
            fields.value = this.props.keyProp;
        } else if (this.props.valueProp) {
            fields.value = this.props.valueProp;
        }

        // For remote search, start empty and let the filtering callback issue
        // the request once the user has typed enough characters.
        const dataSource: any = this.props.url
            ? []
            : this.state.dataSource;

        // Determine if we use CheckBox mode (the onSelect/preventInternalSelect pattern)
        const useCheckBox = !!this.props.onSelect;
        const showSearchDropdown = useCheckBox || !!this.props.onClick;

        // Item template support
        let itemTemplate: any = undefined;
        if (this.props.template) {
            itemTemplate = this.props.template;
        } else if (this.props.templateFunction) {
            itemTemplate = (data: any) => {
                return this.props.templateFunction(data);
            };
        }

        return (
            <div className="multi-select fr-margin" id={this.props.id}>
                <MultiSelectComponent
                    ref={this._msRef}
                    dataSource={dataSource}
                    fields={fields}
                    value={this.state.selectedValues}
                    mode={useCheckBox ? 'CheckBox' : 'Default'}
                    allowFiltering={true}
                    filtering={this._onFiltering}
                    filterBarPlaceholder=""
                    select={this._onSelectingHandler}
                    removing={this._onRemovingHandler}
                    change={this._onChangeHandler}
                    itemTemplate={itemTemplate}
                    noRecordsTemplate=" "
                    showClearButton={false}
                    openOnClick={showSearchDropdown}
                    showDropDownIcon={showSearchDropdown}
                >
                    {useCheckBox && <Inject services={[CheckBoxSelection]} />}
                </MultiSelectComponent>
                {this.props.onClick && (
                    <a className="btn btn-info" onClick={this._onButtonClick}>
                        {this.props.btnLabel}
                    </a>
                )}
            </div>
        );
    }
}

export default MultiSelect;
